import { Parser } from "expr-eval-fork";

// Maximální délka výrazu - obrana proti DoS / depth bomb / unbounded parsing.
export const MAX_EXPRESSION_LENGTH = 1000;

// Regex pro detekci a extrakci ZMENA/TRVA/POSLEDNI funkcí.
// Tyto se nahrazují PŘED parsováním (jejich výsledek je číslo / boolean ze serveru).
// Globální flag, abychom našli VŠECHNY výskyty (původní implementace nahrazovala jen první - bug).
export const POSLEDNIregex = /POSLEDNI\(([^)]+)\)/g;
export const ZMENAregex = /ZMENA\(([^,]+),\s*([^,]+),\s*([*+-])\)/g;
export const TRVAregex = /TRVA\(([^,]+),\s*([^,]+),\s*([^)]+)\)/g;

// Sdílený Parser nakonfigurovaný restriktivně:
// - žádné assignment (=, :=)
// - žádný `in` operator
// - žádné conditional (ternary `? :`) — komplikuje analýzu, jednoduchý true/false stačí
// expr-eval je AST-based, NEPOUŽÍVÁ JS eval / new Function() — zcela bezpečný proti code injection.
const parser = new Parser({
  operators: {
    add: true,
    subtract: true,
    multiply: true,
    divide: true,
    remainder: true,
    power: true,
    comparison: true,
    logical: true,
    assignment: false,
    in: false,
    conditional: false,
  },
});

/**
 * Escape pro regex — topic obsahuje `/`, `.`, `-` apod.
 */
const escapeForRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Nahrazuje placeholder `{topicId}` v expression za daný literál (pomocí callback)
 * a hází chybu, pokud zbude nějaký nezpracovaný placeholder.
 */
const substituteTopics = (expression: string, getLiteral: (topic: string) => string | undefined): string => {
  let result = expression;
  // Najdi všechny {...} a nahraď je. Limitujeme počet iterací proti pathological inputs.
  const placeholderRegex = /\{([^}]+)\}/g;
  result = result.replace(placeholderRegex, (match, topic: string) => {
    const literal = getLiteral(topic);
    if (literal === undefined) {
      // Necháme placeholder na místě → následná kontrola ho odhalí
      return match;
    }
    return literal;
  });
  return result;
};

/**
 * Nahradí ZMENA/TRVA/POSLEDNI funkční volání placeholderem `1` — pro účely validace
 * (na serveru se nahrazují skutečnými hodnotami z DB / memory dříve než se evaluuje).
 */
const replaceMacrosWithPlaceholder = (expression: string): string =>
  expression.replace(ZMENAregex, "1").replace(TRVAregex, "1").replace(POSLEDNIregex, "1");

/**
 * Validace výrazu: parser projde, všechny topic vars jsou poskytnuté, žádný runtime error.
 * Použito klientem (RuleSetup) i serverem (před uložením pravidla v DB).
 */
export const validateExpression = (expression: string, topics: RuleTopics): boolean => {
  if (!expression || typeof expression !== "string") return false;
  if (expression.length > MAX_EXPRESSION_LENGTH) return false;

  try {
    const cleaned = replaceMacrosWithPlaceholder(expression);
    const substituted = substituteTopics(cleaned, (topic) => {
      const type = topics[topic];
      if (!type) return undefined;
      return type === "boolean" ? "true" : "1";
    });

    // Pokud po substituci zbývají {topic} placeholdery → neznámý topic
    if (/\{[^}]+\}/.test(substituted)) return false;

    parser.parse(substituted).evaluate();
    return true;
  } catch {
    return false;
  }
};

/**
 * Bezpečné runtime vyhodnocení rule výrazu (na serveru).
 * Caller MUSÍ předat již ZMENA/TRVA/POSLEDNI rozbalené (nahrazené konkrétními hodnotami).
 *
 * Vrací boolean výsledek nebo throw při syntax / runtime chybě.
 * Topic placeholdery `{topic}` se nahrazují JSON literály z `context` (čísla, booleany).
 * Pokud topic v contextu chybí (undefined/null), vrací se `false` (pravidlo se neaktivuje).
 */
export const evaluateRuleExpression = (expression: string, context: RuleContext): boolean => {
  if (!expression || typeof expression !== "string") {
    throw new Error("Empty or invalid expression");
  }
  if (expression.length > MAX_EXPRESSION_LENGTH) {
    throw new Error(`Expression too long (max ${MAX_EXPRESSION_LENGTH} chars)`);
  }

  let missingTopic = false;
  const substituted = substituteTopics(expression, (topic) => {
    const value = context[topic];
    if (value === undefined || value === null) {
      missingTopic = true;
      return undefined;
    }
    // Čísla i booleany serializovatelné přes JSON jsou bezpečné literály v expr-eval
    return JSON.stringify(value);
  });

  if (missingTopic) return false;

  // Sanity check — žádné nezpracované placeholdery
  if (/\{[^}]+\}/.test(substituted)) return false;

  const result = parser.parse(substituted).evaluate();
  return result === true;
};

/**
 * Vyhodnocení výrazu s pojmenovanými proměnnými (pro `math.formula` uzel automatizací).
 * Stejný restriktivní parser jako pravidla (žádný eval/Function). Vrací number|boolean,
 * nebo `undefined` při chybě / neúplném contextu.
 */
export const evaluateFormula = (
  expression: string,
  vars: Record<string, number | boolean>
): number | boolean | undefined => {
  if (!expression || expression.length > MAX_EXPRESSION_LENGTH) return undefined;
  try {
    const expr = parser.parse(expression);
    const result = expr.evaluate(vars as unknown as Parameters<typeof expr.evaluate>[0]);
    if (typeof result === "number" || typeof result === "boolean") return result;
    return undefined;
  } catch {
    return undefined;
  }
};

// Pomocné re-exporty pro místa, kde se historicky volalo escape
export { escapeForRegex };
